/*
 * Picks a locale and rewrites the page into it.
 *
 * Loaded as a classic script at the end of <body>, deliberately: a deferred or
 * module script runs after first paint, which would show a flash of English to
 * everyone else. The markup ships in English so that crawlers and no-JS
 * visitors still get a complete page.
 *
 * Markup contract:
 *   data-i18n="hero.cta"                      -> replaces textContent
 *   data-i18n-attr="alt:store.badgeAlt"       -> replaces that attribute
 *                   (comma-separated for several attributes on one element)
 *
 * Locale resolution, in order:
 *   1. the visitor's own choice on this site      (localStorage nocap-locale)
 *   2. the language they picked inside Board Bash (localStorage
 *      boardbash-locale-preference — same origin, so it is readable here)
 *   3. the browser's accepted languages
 *   4. English
 *
 * Step 2 is read-only on purpose: the key belongs to the game (its
 * `storage-util.ts` owns the `boardbash-` prefix), so the portal borrows it as
 * a hint but never writes it back.
 */
(function () {
    'use strict';

    var I18N = window.NOCAP_I18N;
    var STORAGE_KEY = 'nocap-locale';
    var BOARDBASH_STORAGE_KEY = 'boardbash-locale-preference';
    var FALLBACK = 'en';

    var SUPPORTED = I18N.locales.map(function (entry) {
        return entry.code;
    });

    function isSupported(code) {
        return SUPPORTED.indexOf(code) !== -1;
    }

    /** localStorage throws in some privacy modes, so every access is guarded. */
    function readStored(key) {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            return null;
        }
    }

    function writeStored(key, value) {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            /* A visitor who blocks storage just gets detection again next time. */
        }
    }

    function detectFromBrowser() {
        var preferred = navigator.languages && navigator.languages.length
            ? navigator.languages
            : [navigator.language || ''];
        for (var i = 0; i < preferred.length; i++) {
            var base = String(preferred[i]).toLowerCase().split('-')[0];
            if (isSupported(base)) return base;
        }
        return FALLBACK;
    }

    function resolveLocale() {
        var own = readStored(STORAGE_KEY);
        if (own !== null && isSupported(own)) return own;

        var fromGame = readStored(BOARDBASH_STORAGE_KEY);
        if (fromGame !== null && isSupported(fromGame)) return fromGame;

        return detectFromBrowser();
    }

    /** 'boardbash.play' -> the string, falling back to English on a gap. */
    function lookup(locale, path) {
        var parts = path.split('.');
        var dictionaries = locale === FALLBACK
            ? [I18N.strings[locale]]
            : [I18N.strings[locale], I18N.strings[FALLBACK]];
        for (var i = 0; i < dictionaries.length; i++) {
            var value = dictionaries[i];
            for (var p = 0; p < parts.length && value != null; p++) {
                value = value[parts[p]];
            }
            if (typeof value === 'string') return value;
        }
        return '';
    }

    function applyLocale(locale) {
        var root = document.documentElement;
        root.lang = locale;

        document.title = lookup(locale, 'meta.title');
        var description = document.querySelector('meta[name="description"]');
        if (description) description.setAttribute('content', lookup(locale, 'meta.description'));

        var textNodes = document.querySelectorAll('[data-i18n]');
        for (var i = 0; i < textNodes.length; i++) {
            textNodes[i].textContent = lookup(locale, textNodes[i].getAttribute('data-i18n'));
        }

        var attrNodes = document.querySelectorAll('[data-i18n-attr]');
        for (var n = 0; n < attrNodes.length; n++) {
            var pairs = attrNodes[n].getAttribute('data-i18n-attr').split(',');
            for (var p = 0; p < pairs.length; p++) {
                var pair = pairs[p].trim().split(':');
                if (pair.length === 2) {
                    attrNodes[n].setAttribute(pair[0].trim(), lookup(locale, pair[1].trim()));
                }
            }
        }
    }

    function buildPicker(locale) {
        var slot = document.querySelector('[data-lang-slot]');
        if (!slot) return;

        var select = document.createElement('select');
        select.className = 'lang-select';
        select.setAttribute('aria-label', lookup(locale, 'ui.languageLabel'));

        for (var i = 0; i < I18N.locales.length; i++) {
            var entry = I18N.locales[i];
            var option = document.createElement('option');
            option.value = entry.code;
            // Each language names itself, as in the game's own picker.
            option.textContent = entry.name;
            option.lang = entry.code;
            if (entry.code === locale) option.selected = true;
            select.appendChild(option);
        }

        select.addEventListener('change', function () {
            var chosen = select.value;
            if (!isSupported(chosen)) return;
            writeStored(STORAGE_KEY, chosen);
            applyLocale(chosen);
            select.setAttribute('aria-label', lookup(chosen, 'ui.languageLabel'));
        });

        slot.appendChild(select);
    }

    var active = resolveLocale();
    applyLocale(active);
    buildPicker(active);
})();
